import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../../../../constants';
import RacePlannerCharacterSearch from './RacePlannerSearch/RacePlannerCharacterSearch';
import RacePlannerCharacterDisplay from './RacePlannerCharacterDisplay';
import { useRaceEngineContext } from '../RaceEngine/RaceEngineContext';

export const RANK_ORDER = ['S', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];

export const EMPTY_FILTERS = {
    turf: null, dirt: null,
    short_distance: null, mile: null, medium_distance: null, long_distance: null,
    runner: null, leader: null, betweener: null, chaser: null,
};

const RacePlannerCharacters = () => {
    const { setSelectedCharacter: setCtxCharacter, setSelectedAptitudes } = useRaceEngineContext();

    const [characters, setCharacters] = useState([]);
    const [cards, setCards] = useState([]);
    const [allAptitudes, setAllAptitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [selectedCard, setSelectedCard] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const [charsRes, cardsRes, aptsRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/characters`),
                    fetch(`${API_BASE_URL}/cards`),
                    fetch(`${API_BASE_URL}/aptitudes`),
                ]);
                const [charsData, cardsData, aptsData] = await Promise.all([
                    charsRes.json(), cardsRes.json(), aptsRes.json(),
                ]);
                setCharacters(charsData.filter(c => c.Player_type !== 'NPC'));
                setCards(cardsData);
                setAllAptitudes(aptsData);
            } catch (err) {
                console.error('Error loading RacePlanner data:', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const handleSelectCharacter = (character) => {
        setSelectedCharacter(character);
        const charCards = cards.filter(c => c.Id_Personaje === character.Id_Personaje);
        const originalCard = charCards.find(c => c.reference === 'Original');
        setSelectedCard(originalCard || charCards[0]);
        setFilters(EMPTY_FILTERS);
        setIsSearching(false);

        // Publicar al contexto compartido para que RacePlannerTimeline lo consuma
        setCtxCharacter(character);
        const apt =
            allAptitudes.find(a => a.Id_Personaje === character.Id_Personaje && !a.card_id) ||
            allAptitudes.find(a => a.Id_Personaje === character.Id_Personaje);
        setSelectedAptitudes(apt || null);
    };

    /** Characters that pass ALL active aptitude filters */
    const filteredCharacters = useMemo(() => {
        const active = Object.entries(filters).filter(([, v]) => v !== null);
        if (active.length === 0) return characters;

        return characters.filter(char => {
            const apt =
                allAptitudes.find(a => a.Id_Personaje === char.Id_Personaje && !a.card_id) ||
                allAptitudes.find(a => a.Id_Personaje === char.Id_Personaje);
            if (!apt) return false;

            return active.every(([key, filterVal]) => {
                const charIdx = RANK_ORDER.indexOf(apt[key]);
                
                // Maneja tanto el antiguo formato string como el nuevo objeto condicional
                const targetRank = typeof filterVal === 'object' ? filterVal.rank : filterVal;
                const op = typeof filterVal === 'object' ? filterVal.op : '>=';
                const targetIdx  = RANK_ORDER.indexOf(targetRank);
                
                if (charIdx === -1 || targetIdx === -1) return false;
                
                // En RANK_ORDER un index *menor* es un *mejor* rank (0 -> S, 7 -> G)
                if (op === '>=') return charIdx <= targetIdx;
                if (op === '<=') return charIdx >= targetIdx;
                if (op === '==') return charIdx === targetIdx;
                return charIdx <= targetIdx;
            });
        });
    }, [characters, filters, allAptitudes]);

    const activeFilterCount = Object.values(filters).filter(Boolean).length;

    return (
        <div className="relative space-y-8">

            {/* ── Sticky Search + Filter Panel ─────────────────────── */}
            <section className="sticky top-0 z-30 -mx-6 px-6 pt-3 pb-5">
                <div className="absolute inset-0 backdrop-blur-xl bg-gradient-to-b from-white/80 dark:from-gray-950/85 via-white/40 dark:via-gray-950/50 to-transparent pointer-events-none" />
                <div className="relative">
                    <RacePlannerCharacterSearch
                        filteredCharacters={filteredCharacters}
                        cards={cards}
                        onSelect={handleSelectCharacter}
                        onSearchActive={setIsSearching}
                        filters={filters}
                        onFilterChange={setFilters}
                        activeFilterCount={activeFilterCount}
                        totalCharacters={characters.length}
                        emptyFilters={EMPTY_FILTERS}
                    />
                </div>
            </section>

            <AnimatePresence mode="wait">
                {loading ? (
                    /* ── Loading ──────────────────────────────────────── */
                    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="flex flex-col items-center justify-center py-40 gap-8">
                        <div className="relative w-14 h-14">
                            <div className="absolute inset-0 rounded-full border border-gray-200 dark:border-white/10" />
                            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                                className="absolute inset-0 rounded-full border-t-2 border-[#48E5B9]" />
                            <motion.div animate={{ rotate: -360 }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                                className="absolute inset-[5px] rounded-full border-t border-[#FAB0C0]/60" />
                        </div>
                        <motion.p animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 2, repeat: Infinity }}
                            className="text-gray-400 dark:text-white/30 text-[10px] uppercase tracking-[0.4em] font-bold">
                            Cargando datos
                        </motion.p>
                    </motion.div>
                ) : (
                    <motion.div key="content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-12">

                        {/* Selected character */}
                        {selectedCharacter && !isSearching && (
                            <RacePlannerCharacterDisplay
                                character={selectedCharacter}
                                cards={cards.filter(c => c.Id_Personaje === selectedCharacter.Id_Personaje)}
                                selectedCard={selectedCard}
                                onCardChange={setSelectedCard}
                            />
                        )}

                        {/* Empty state */}
                        {!selectedCharacter && !isSearching && activeFilterCount === 0 && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                                className="relative text-center py-36 rounded-[40px] overflow-hidden border border-gray-100 dark:border-white/[0.04]">
                                <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_50%,rgba(72,229,185,0.04),transparent)]" />
                                <div className="relative inline-flex items-center justify-center mb-8">
                                    <div className="relative w-20 h-20 flex items-center justify-center">
                                        <div className="absolute inset-0 rounded-full border border-gray-200 dark:border-white/[0.08]" />
                                        <motion.div animate={{ scale: [1, 1.2, 1], opacity: [1, 0.3, 1] }}
                                            transition={{ duration: 3.5, repeat: Infinity }}
                                            className="absolute inset-0 rounded-full border border-[#48E5B9]/25" />
                                        <svg className="w-7 h-7 text-gray-300 dark:text-white/15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-gray-400 dark:text-white/20 font-black uppercase tracking-[0.4em] text-[10px]">
                                    Selecciona una Umamusume
                                </p>
                                <p className="mt-3 text-gray-300 dark:text-white/[0.08] text-xs tracking-widest">
                                    Busca por nombre o filtra por aptitud
                                </p>
                            </motion.div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RacePlannerCharacters;
